import { createAtom } from './base';

// Account-wide content versioning settings (backend key `content_versioning`):
// { keep_history, require_approval, exempt_user_ids, gate_ai_edits,
//   max_previous_versions, release_roles }. Default off — a replace goes live
// at once and nothing is kept.
//
// The shared atoms deep-merge current and updated state with lodash `merge`
// (when reading, and again on save), which merges arrays index by index:
// unticking a user from `exempt_user_ids` (or a role from `release_roles`)
// would leave the last entry in place. So an edited list is padded with nulls
// to the current length (null overwrites in `merge`) and every read drops
// them — `contentVersioningState`.
const LIST_KEYS = ['exempt_user_ids', 'release_roles'] as const;

export const {
  currentContent_versioningSettingsAtom: currentContentVersioningSettingsAtom,
  updatedContent_versioningSettingsAtom: updatedContentVersioningSettingsAtom,
  content_versioningSettingsAtom: contentVersioningSettingsAtom,
} = createAtom('content_versioning', {
  updater: (currentAtom: any, updatedAtom: any, get: any, set: any, action: any) => {
    const payload = { ...action.payload };
    LIST_KEYS.forEach((key) => {
      if (Array.isArray(payload[key])) {
        const currentLength = (get(currentAtom)?.[key] || []).length;
        const items = payload[key].filter((item: unknown) => item !== null);
        payload[key] = [...items, ...Array(Math.max(0, currentLength - items.length)).fill(null)];
      }
    });
    set(updatedAtom, { ...(get(updatedAtom) || {}), ...payload });
  },
});

export const contentVersioningState = (get: any) => {
  const current = get(currentContentVersioningSettingsAtom) || {};
  const updated = get(updatedContentVersioningSettingsAtom);
  const state = updated ? { ...current, ...updated } : { ...current };
  LIST_KEYS.forEach((key) => {
    if (Array.isArray(state[key])) state[key] = state[key].filter((item: unknown) => item !== null);
  });
  return state;
};

/** Changes to save, or undefined when nothing was edited (SaveChangesBar). */
export const getContentVersioningSettings = (get: any) =>
  get(updatedContentVersioningSettingsAtom) ? contentVersioningState(get) : undefined;
